// TS twin of iOS `RepeatButton.swift`.
// Press-and-hold button that fires once on press, then repeats after a
// 500ms initial delay at 100ms intervals while held. Matches iOS UX so a
// joystick-pad nudge feels the same on both platforms.

import { useCallback, useEffect, useRef } from 'react'
import type { CSSProperties, PointerEvent, ReactNode } from 'react'

interface RepeatButtonProps {
  onPress: () => void
  children: ReactNode
  ariaLabel: string
  initialDelayMs?: number
  repeatIntervalMs?: number
  style?: CSSProperties
  testId?: string
}

export function RepeatButton({
  onPress,
  children,
  ariaLabel,
  initialDelayMs = 500,
  repeatIntervalMs = 100,
  style,
  testId,
}: RepeatButtonProps) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const onPressRef = useRef(onPress)
  onPressRef.current = onPress

  const cleanup = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  useEffect(() => cleanup, [cleanup])

  const handleDown = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onPressRef.current()
      cleanup()
      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(() => {
          onPressRef.current()
        }, repeatIntervalMs)
      }, initialDelayMs)
    },
    [cleanup, initialDelayMs, repeatIntervalMs],
  )

  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      onPointerDown={handleDown}
      onPointerUp={cleanup}
      onPointerLeave={cleanup}
      onPointerCancel={cleanup}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255, 255, 255, 0.15)',
        color: 'white',
        border: 'none',
        borderRadius: 8,
        fontSize: 16,
        fontWeight: 700,
        touchAction: 'none',
        userSelect: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  )
}
